/* eslint-disable */

/**
 * Colour + Size variant picker.
 *
 * Availability is never computed on the client. Every time a selection
 * changes, the section is re-rendered through the Section Rendering API with
 * `option_values`, and Shopify returns the correct contextual availability for
 * the new selection. That keeps the component correct past 250 variants and
 * removes the need for any variant data in the DOM.
 *
 * Dependencies:
 * - media-gallery (optional, for colour gallery switching)
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - noSelectedVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.section = this.closest('.js-product') || this.closest('quick-add-drawer');
      this.optionsEl = this.querySelector('.js-option-selectors');

      this.addEventListener('change', this.handleChange.bind(this));

      this.popstateHandler = this.handlePopstate.bind(this);
      window.addEventListener('popstate', this.popstateHandler);

      this.syncCrossedColors();
      this.updateSizeLabel();
      this.updateAddToCartButton();
      this.updateGallery();
      this.dispatchVariantChange();
      this.selectColorFromUrl();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.popstateHandler);
    }

    /**
     * The variant state Liquid rendered for the current selection.
     * @returns {object}
     */
    get state() {
      const el = this.querySelector('.js-variant-state');
      if (!el) return { id: null, available: false };

      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return { id: null, available: false };
      }
    }

    /**
     * Whether the size requirement is satisfied.
     *
     * This is deliberately not "is a size radio checked". Shopify always
     * resolves a default variant server-side, so after a re-render a size can
     * be checked that the shopper never picked; crossing colours out on the
     * strength of that default marks most of the range unavailable on first
     * paint. Nothing preselects a size, so the flag starts false and is only
     * flipped when the shopper picks one themselves.
     *
     * @returns {boolean}
     */
    get sizeSelected() {
      const sizeSelector = this.querySelector('[data-size-selector]');

      // No size option on this product at all, so there is nothing to choose
      // and Add to Cart must not sit disabled waiting for it.
      if (!sizeSelector) return true;

      if (this.dataset.sizeChosen !== 'true') return false;
      return Boolean(sizeSelector.querySelector('.js-option:checked'));
    }

    /**
     * IDs of every currently checked option value, in option order.
     * @returns {Array<string>}
     */
    getSelectedOptionValueIds() {
      return Array.from(this.querySelectorAll('.js-option:checked'))
        .map((input) => input.dataset.optionValueId)
        .filter(Boolean);
    }

    /**
     * Handles 'change' events from any option input.
     * @param {Event} evt - Event object.
     */
    async handleChange(evt) {
      if (!evt.target.matches('.js-option')) return;

      const colorChanged = Boolean(evt.target.closest('[data-color-selector]'));

      // Picking a size is what turns the diagonal lines on. Picking a colour
      // does not, so a shopper browsing colours never sees the range crossed
      // out before they have expressed a size.
      if (evt.target.closest('[data-size-selector]')) {
        this.dataset.sizeChosen = evt.target.checked ? 'true' : 'false';
      }

      const focusedId = evt.target.dataset.optionValueId;
      await this.refreshOptions(focusedId);

      this.updateUrl();
      this.updateVariantInput();
      this.updateColorLabel();
      this.updateSizeLabel();
      this.updateGallery();
      this.updateAddToCartButton();
      this.dispatchVariantChange();
    }

    /**
     * Re-renders the option selectors and price for the current selection via
     * the Section Rendering API, then restores focus.
     * @param {string} focusedOptionValueId - Option value to refocus after swap.
     */
    async refreshOptions(focusedOptionValueId) {
      const ids = this.getSelectedOptionValueIds();
      if (!ids.length) return;

      const url = `${this.dataset.url}?section_id=${encodeURIComponent(this.dataset.sectionId)}&option_values=${ids.join(',')}`;

      // Ignore a response that lands after a newer one; shoppers click fast.
      const token = (this.fetchToken || 0) + 1;
      this.fetchToken = token;

      this.setBusy(true);

      try {
        const response = await fetch(url);
        if (!response.ok) return;

        const text = await response.text();
        if (this.fetchToken !== token) return;

        const doc = new DOMParser().parseFromString(text, 'text/html');
        this.swapOptions(doc, focusedOptionValueId);
        this.swapPrice(doc);
        this.swapSku(doc);
      } catch (e) {
        // Leave the current DOM in place; the shopper's selection still stands.
      } finally {
        if (this.fetchToken === token) this.setBusy(false);
      }
    }

    /**
     * Replaces the option selectors with the freshly rendered markup.
     * @param {Document} doc - Parsed section response.
     * @param {string} focusedOptionValueId - Option value to refocus.
     * @param {boolean} preserveSelection - Restore checked option values.
     */
    swapOptions(doc, focusedOptionValueId, preserveSelection = true) {
      const newOptions = doc.querySelector('.js-option-selectors');
      const newState = doc.querySelector('.js-variant-state');
      if (!newOptions) return;

      const active = document.activeElement;
      const hadFocus = active && this.contains(active);

      // Liquid deliberately never renders a size as checked, so nothing can
      // preselect one. That also means the fresh markup comes back with the
      // shopper's own size cleared, which would silently undo the click that
      // triggered this render. Their selection is carried across here.
      const previousIds = preserveSelection ? this.getSelectedOptionValueIds() : [];

      this.optionsEl.innerHTML = newOptions.innerHTML;

      previousIds.forEach((id) => {
        const input = this.querySelector(`.js-option[data-option-value-id="${id}"]`);
        // Restored even when the newly chosen colour does not stock it. That is
        // how crocs.com behaves: your size stays picked as you browse colours
        // and is shown struck through where it cannot be bought, rather than
        // being silently cleared and asked for again.
        if (input) input.checked = true;
      });

      // Only if the value has gone from the option list entirely does the page
      // go back to waiting for a size.
      const sizeSelector = this.querySelector('[data-size-selector]');
      if (sizeSelector && !sizeSelector.querySelector('.js-option:checked')) {
        this.dataset.sizeChosen = 'false';
      }

      const stateEl = this.querySelector('.js-variant-state');
      if (newState && stateEl) stateEl.textContent = newState.textContent;

      this.syncCrossedColors();
      this.updateSizeLabel();

      if (hadFocus && focusedOptionValueId) {
        const refocus = this.querySelector(`.js-option[data-option-value-id="${focusedOptionValueId}"]`);
        if (refocus) refocus.focus({ preventScroll: true });
      }
    }

    /**
     * Replaces the rendered price block, which carries compare-at and sold-out
     * state already formatted by Liquid.
     * @param {Document} doc - Parsed section response.
     */
    swapPrice(doc) {
      this.swapFragment(doc, '.product-info__price');
    }

    /**
     * Replaces a server-rendered fragment from the section response. Used for
     * anything Liquid already formats correctly for the current selection, so
     * the client never has to reformat money or rebuild a SKU string.
     * @param {Document} doc - Parsed section response.
     * @param {string} selector - Fragment to replace.
     */
    swapFragment(doc, selector) {
      if (!this.section) return;

      const current = this.section.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    }

    /**
     * Replaces the vendor/SKU block. The SKU's colour half changes with the
     * colour, so it has to follow the tiles.
     * @param {Document} doc - Parsed section response.
     */
    swapSku(doc) {
      this.swapFragment(doc, '.product-vendor-sku');
    }

    /**
     * Marks colours that exist but are not buyable in the chosen size with a
     * diagonal line. Only applied once a size is actually selected, since
     * before that the server falls back to a default size the shopper never
     * picked.
     */
    syncCrossedColors() {
      const showCrosses = this.sizeSelected;

      this.querySelectorAll('[data-color-selector] .js-option').forEach((input) => {
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        const crossed = showCrosses && input.dataset.unavailableInSize === 'true';
        label.classList.toggle('opt-label--crossed', crossed);

        // The note lives inside the label, so unhiding it appends "unavailable
        // in the selected size" to the control's accessible name. The colour
        // stays selectable: choosing it re-renders and drops the size.
        const note = label.querySelector('.js-unavailable-note');
        if (note) note.hidden = !crossed;
      });
    }

    /**
     * Updates the colour name shown beside the "Color" legend.
     */
    updateColorLabel() {
      const labelEl = this.querySelector('.js-color-text');
      if (!labelEl) return;

      const checked = this.querySelector('[data-color-selector] .js-option:checked');
      labelEl.textContent = checked ? checked.dataset.colorName || checked.value : '';
    }

    /**
     * Writes the chosen size next to the "Size" legend, the way crocs.com
     * reads "Size: Women's 10". Stays empty until the shopper chooses, since
     * the server always has some size selected of its own accord.
     */
    updateSizeLabel() {
      const labelEl = this.querySelector('.js-size-text');
      if (!labelEl) return;

      const checked = this.querySelector('[data-size-selector] .js-option:checked');
      labelEl.textContent = (this.sizeSelected && checked) ? `: ${checked.value}` : '';
    }

    /**
     * Switches the product gallery to the selected colour's gallery.
     */
    updateGallery() {
      if (!this.section) return;

      const checked = this.querySelector('[data-color-selector] .js-option:checked');
      if (!checked) return;

      // `data-media-group` is written by color-galleries.liquid from the
      // matched gallery's colour name, normalised identically on both sides.
      // Falling back to the option value would reintroduce the drift that left
      // the gallery blank when a metaobject name differed from the option.
      const group = (checked.dataset.mediaGroup
        || checked.dataset.colorName
        || checked.value
        || '').toLowerCase().trim();

      if (typeof this.section.updateMediaGroup === 'function') {
        this.section.updateMediaGroup(group);
        return;
      }

      const gallery = this.section.querySelector('media-gallery');
      if (gallery && typeof gallery.setActiveMediaGroup === 'function') {
        gallery.setActiveMediaGroup(group);
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state. The button
     * stays disabled until a complete, available Colour + Size pair is chosen.
     */
    updateAddToCartButton() {
      if (!this.section) return;

      const form = this.section.querySelector('.js-product-form-main');
      if (!form) return;

      const addBtn = form.querySelector('[name="add"]');
      if (!addBtn) return;

      const { id, available } = this.state;
      const complete = Boolean(id) && this.sizeSelected;

      addBtn.disabled = !complete || !available;

      if (!this.sizeSelected) {
        addBtn.textContent = theme.strings.noSelectedVariant;
      } else if (!id) {
        addBtn.textContent = theme.strings.noVariant;
      } else if (!available) {
        addBtn.textContent = theme.strings.noStock;
      } else {
        addBtn.textContent = addBtn.dataset.addToCartText;
      }
    }

    /**
     * Writes the selected variant id into the product forms.
     */
    updateVariantInput() {
      if (!this.section) return;

      const { id } = this.state;
      const forms = this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.value = id || '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Keeps the selected variant in the URL without adding one browser-history
     * entry per colour or size change. Back should leave the product page in
     * one click, regardless of how many options the shopper tried.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;

      const { id } = this.state;
      let url = this.dataset.url;

      if (id) {
        // A full colour + size pair resolves to a variant, and ?variant= is
        // Shopify's own canonical form — it restores both options server-side.
        url = `${this.dataset.url}?variant=${id}`;
      } else {
        // Only a colour is chosen, so there is no variant to point at yet.
        // crocs.com carries the colour as ?cid=0UJ; the code is the colour
        // half of the SKU. Restored on load by selectColorFromUrl().
        const code = this.selectedColorCode();
        if (code) url = `${this.dataset.url}?cid=${encodeURIComponent(code)}`;
      }

      if (window.location.pathname + window.location.search === url) return;
      window.history.replaceState({ variantPicker: true, variantId: id || null }, '', url);
    }

    /**
     * The colour code of the selected colour, e.g. "0UJ" from "PURPLE (0UJ)".
     * @returns {?string}
     */
    selectedColorCode() {
      const checked = this.querySelector('[data-color-selector] .js-option:checked');
      return checked ? checked.dataset.colorCode || null : null;
    }

    /**
     * Applies a ?cid=<code> parameter on first load. Liquid cannot read
     * arbitrary query parameters, so the colour is restored here.
     */
    async selectColorFromUrl() {
      if (this.dataset.updateUrl === 'false') return;

      const params = new URLSearchParams(window.location.search);
      const code = params.get('cid');
      const filteredSize = params.get('filtered_size');
      if (code) {
        const target = this.querySelector(`[data-color-selector] .js-option[data-color-code="${CSS.escape(code)}"]`);
        if (target && !target.checked) {
          target.checked = true;
          await this.handleChange({ target });
        }
      }
      if (filteredSize) {
        const size = Array.from(this.querySelectorAll('[data-size-selector] .js-option'))
          .find(input => input.value === filteredSize && !input.disabled);
        if (size) {
          size.checked = true;
          await this.handleChange({ target: size });
        }
      }
    }

    /**
     * Restores the picker when the shopper navigates back or forward.
     */
    async handlePopstate() {
      if (this.dataset.updateUrl === 'false') return;

      const response = await fetch(`${window.location.pathname}${window.location.search}${window.location.search ? '&' : '?'}section_id=${encodeURIComponent(this.dataset.sectionId)}`);
      if (!response.ok) return;

      // Back/forward restores whatever the URL describes, so the selection in
      // the DOM must not be carried over it.
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swapOptions(doc, null, false);
      this.swapPrice(doc);
      this.swapSku(doc);

      this.updateVariantInput();
      this.updateColorLabel();
      this.updateSizeLabel();
      this.updateGallery();
      this.updateAddToCartButton();
      this.dispatchVariantChange();
    }

    /**
     * Toggles a busy state while the section re-renders.
     * @param {boolean} busy - Whether a fetch is in flight.
     */
    setBusy(busy) {
      this.toggleAttribute('data-loading', busy);
      this.optionsEl.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    /**
     * Dispatches the theme-wide variant change event. The payload keeps the
     * existing contract: consumers read `detail.variant.id`.
     */
    dispatchVariantChange() {
      const { id, available, price } = this.state;
      const checked = this.querySelector('[data-color-selector] .js-option:checked');

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.section ? this.section.querySelector('.js-product-form-main') : null,
          variant: id ? { id, available, price } : null,
          color: checked ? checked.dataset.colorName || checked.value : null,
          product: { url: this.dataset.url }
        }
      }));
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
